import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { CanonicalWork, Series } from "../generated/contracts";
import { readLocalState, writeLocalState } from "../storage";

export type LibrarySnapshot = {
  favorites: Record<string, CanonicalWork>;
  savedSeries: Record<
    string,
    Pick<Series, "id" | "slug" | "title" | "description" | "artworkUrl">
  >;
  history: CanonicalWork[];
  progress: Record<string, number>;
  completed: string[];
  collections: Record<
    string,
    { name: string; workIds: string[]; works?: CanonicalWork[] }
  >;
  reading: Record<string, number>;
};

type LibraryValue = LibrarySnapshot & {
  hydrated: boolean;
  setCloudSession: (enabled: boolean) => void;
  replaceSnapshot: (snapshot: LibrarySnapshot) => void;
  saveCollection: (id: string, name: string, workIds: string[]) => void;
  recordReading: (id: string, position: number) => void;
  toggleFavorite: (work: CanonicalWork) => void;
  isFavorite: (id: string) => boolean;
  toggleSeries: (series: Series) => void;
  isSeriesFavorite: (id: string) => boolean;
  recordProgress: (work: CanonicalWork, seconds: number) => void;
  markComplete: (work: CanonicalWork) => void;
};

const empty: LibrarySnapshot = {
  favorites: {},
  savedSeries: {},
  history: [],
  progress: {},
  completed: [],
  collections: {},
  reading: {},
};
const Context = createContext<LibraryValue | null>(null);

export function LibraryProvider({ children }: React.PropsWithChildren) {
  const [cloudSession, setCloudSession] = useState(false);
  const [snapshot, setSnapshot] = useState(empty);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    readLocalState("library", empty)
      .then((stored) =>
        setSnapshot({
          ...empty,
          ...stored,
          savedSeries: stored.savedSeries || {},
        }),
      )
      .finally(() => setHydrated(true));
  }, []);
  useEffect(() => {
    if (hydrated && !cloudSession)
      writeLocalState("library", snapshot).catch(() => undefined);
  }, [hydrated, snapshot, cloudSession]);

  const toggleFavorite = useCallback(
    (work: CanonicalWork) =>
      setSnapshot((current) => {
        const favorites = { ...current.favorites };
        if (favorites[work.id]) delete favorites[work.id];
        else favorites[work.id] = work;
        return { ...current, favorites };
      }),
    [],
  );
  const toggleSeries = useCallback(
    (series: Series) =>
      setSnapshot((current) => {
        const savedSeries = { ...current.savedSeries };
        if (savedSeries[series.id]) delete savedSeries[series.id];
        else
          savedSeries[series.id] = {
            id: series.id,
            slug: series.slug,
            title: series.title,
            description: series.description,
            artworkUrl: series.artworkUrl,
          };
        return { ...current, savedSeries };
      }),
    [],
  );
  const recordProgress = useCallback(
    (work: CanonicalWork, seconds: number) =>
      setSnapshot((current) => ({
        ...current,
        progress: { ...current.progress, [work.id]: seconds },
        history: [
          work,
          ...current.history.filter((item) => item.id !== work.id),
        ].slice(0, 100),
      })),
    [],
  );
  const markComplete = useCallback(
    (work: CanonicalWork) =>
      setSnapshot((current) => ({
        ...current,
        completed: current.completed.includes(work.id)
          ? current.completed
          : [...current.completed, work.id],
        history: [
          work,
          ...current.history.filter((item) => item.id !== work.id),
        ].slice(0, 100),
      })),
    [],
  );
  const saveCollection = useCallback(
    (id: string, name: string, workIds: string[]) =>
      setSnapshot((s) => {
        const collections = { ...s.collections };
        if (!name.trim()) delete collections[id];
        else {
          const known = [
            ...Object.values(s.favorites),
            ...s.history,
            ...Object.values(s.collections).flatMap((c) => c.works || []),
          ];
          collections[id] = {
            name,
            workIds,
            works: workIds
              .map((id) => known.find((w) => w.id === id))
              .filter((w): w is CanonicalWork => !!w),
          };
        }
        return { ...s, collections };
      }),
    [],
  );
  const recordReading = useCallback(
    (id: string, position: number) =>
      setSnapshot((s) => ({ ...s, reading: { ...s.reading, [id]: position } })),
    [],
  );
  const replaceSnapshot = useCallback(
    (data: LibrarySnapshot) => setSnapshot({ ...empty, ...data }),
    [],
  );
  const value = useMemo(
    () => ({
      ...snapshot,
      hydrated,
      setCloudSession,
      replaceSnapshot,
      saveCollection,
      recordReading,
      toggleFavorite,
      isFavorite: (id: string) => Boolean(snapshot.favorites[id]),
      toggleSeries,
      isSeriesFavorite: (id: string) => Boolean(snapshot.savedSeries[id]),
      recordProgress,
      markComplete,
    }),
    [
      snapshot,
      hydrated,
      setCloudSession,
      replaceSnapshot,
      saveCollection,
      recordReading,
      toggleFavorite,
      toggleSeries,
      recordProgress,
      markComplete,
    ],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useLibrary() {
  const value = useContext(Context);
  if (!value) throw new Error("useLibrary must be inside LibraryProvider");
  return value;
}
